/**
 * Calls `onNearEnd` only in response to a user scroll near the end of a list.
 *
 * An IntersectionObserver invokes its callback immediately when a short list's
 * sentinel is already visible. For paginated lists that behavior can drain the
 * entire API without any user interaction. Listening to scroll instead keeps
 * loading tied to the user's intent.
 */
export function observeInfiniteScroll(
  target,
  onNearEnd,
  {
    root = target?.closest?.(".overflow-auto"),
    threshold = 200,
    axis = "vertical",
  } = {},
) {
  if (!target) return () => {};

  const scrollTarget = root || window;
  const gestureState = getGestureState(scrollTarget);

  const recordUserIntent = () => {
    const now = Date.now();

    // A pause marks a new gesture. Keeping this state outside the React effect
    // prevents a newly appended page from re-arming the gesture that loaded it.
    if (now - gestureState.lastInputAt > 250) {
      gestureState.loadedForGesture = false;
    }
    gestureState.lastInputAt = now;
  };
  const recordPointerIntent = (event) => {
    if (event.buttons > 0) recordUserIntent();
  };

  const handleScroll = () => {
    const isCurrentUserGesture = Date.now() - gestureState.lastInputAt <= 250;
    if (!isCurrentUserGesture || gestureState.loadedForGesture) return;

    const distanceFromEnd = root
      ? axis === "horizontal"
        ? getHorizontalDistanceFromEnd(root)
        : root.scrollHeight - root.scrollTop - root.clientHeight
      : target.getBoundingClientRect().top - window.innerHeight;

    if (distanceFromEnd <= threshold) {
      gestureState.loadedForGesture = true;
      onNearEnd();
    }
  };

  scrollTarget.addEventListener("wheel", recordUserIntent, { passive: true });
  scrollTarget.addEventListener("touchmove", recordUserIntent, { passive: true });
  scrollTarget.addEventListener("pointermove", recordPointerIntent, { passive: true });
  scrollTarget.addEventListener("keydown", recordUserIntent);
  scrollTarget.addEventListener("scroll", handleScroll, { passive: true });
  return () => {
    scrollTarget.removeEventListener("wheel", recordUserIntent);
    scrollTarget.removeEventListener("touchmove", recordUserIntent);
    scrollTarget.removeEventListener("pointermove", recordPointerIntent);
    scrollTarget.removeEventListener("keydown", recordUserIntent);
    scrollTarget.removeEventListener("scroll", handleScroll);
  };
}

function getHorizontalDistanceFromEnd(root) {
  const maxScroll = Math.max(0, root.scrollWidth - root.clientWidth);
  const isRtl = window.getComputedStyle(root).direction === "rtl";
  if (!isRtl) return maxScroll - root.scrollLeft;

  // Browsers expose RTL scrolling either as 0..-max or max..0.
  return root.scrollLeft < 0
    ? maxScroll - Math.abs(root.scrollLeft)
    : root.scrollLeft;
}

const gestureStates = new WeakMap();

function getGestureState(scrollTarget) {
  let state = gestureStates.get(scrollTarget);
  if (!state) {
    state = { lastInputAt: 0, loadedForGesture: false };
    gestureStates.set(scrollTarget, state);
  }
  return state;
}
